import { useEffect, useState } from "react";
import styles from "./CallingState.module.css";

type Props = {
  agentName: string;
  phoneE164: string;
  onComplete: () => void;
  onRetry: () => void;
};

const maskPhone = (raw: string): string => {
  const digits = raw.replace(/\D+/g, "");
  if (digits.length <= 4) return raw || "your phone";
  const country = raw.startsWith("+") ? raw.slice(0, raw.indexOf(" ") > 0 ? raw.indexOf(" ") : 3) : `+${digits.slice(0, 2)}`;
  const last3 = digits.slice(-3);
  return `${country} •• ••• ${last3}`;
};

const RETRY_LOCK_MS = 25_000;
const COMPLETE_AFTER_MS = 6_000;

export const CallingState = ({ agentName, phoneE164, onComplete, onRetry }: Props): JSX.Element => {
  const [retryReady, setRetryReady] = useState<boolean>(false);
  const initial = (agentName.trim()[0] ?? "A").toUpperCase();

  useEffect(() => {
    const lock = setTimeout(() => setRetryReady(true), RETRY_LOCK_MS);
    const done = setTimeout(() => onComplete(), COMPLETE_AFTER_MS);
    return () => {
      clearTimeout(lock);
      clearTimeout(done);
    };
  }, [onComplete]);

  return (
    <div className={styles.root}>
      <span className={styles.eyebrow}>Outbound call · ringing</span>

      <div className={styles.avatarStack}>
        <span className={styles.baseRing} aria-hidden />
        <span className={`${styles.ring} ${styles.ringOuter}`} aria-hidden />
        <span className={`${styles.ring} ${styles.ringInner}`} aria-hidden />
        <div className={styles.avatar} aria-hidden>
          {initial}
        </div>
      </div>

      <div className={styles.body}>
        <div className={styles.callingLine}>
          <Wave />
          <span className={styles.callingText}>Calling {maskPhone(phoneE164)}</span>
        </div>
        <p className={styles.helper}>
          Pick up when it rings.{" "}
          <button
            type="button"
            className={styles.retryLink}
            disabled={!retryReady}
            onClick={onRetry}
          >
            {retryReady ? "Retry call" : "Retry available in a moment"}
          </button>
        </p>
      </div>
    </div>
  );
};

const Wave = (): JSX.Element => (
  <span className={styles.wave} aria-hidden>
    <span className={`${styles.bar} ${styles.bar1}`} />
    <span className={`${styles.bar} ${styles.bar2}`} />
    <span className={`${styles.bar} ${styles.bar3}`} />
  </span>
);
